/**
 * MEVIA v5.0 - Fase 2: Employee Aspirational Questionnaire
 * 
 * 7 questions to capture employee's aspirations and goals for the next 12 months.
 * This component is shown after the employee completes their Dimensions evaluation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import {
  ChevronRight, ChevronLeft, Target, Sparkles, TrendingUp,
  Heart, MessageSquare, CheckCircle2, Loader2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';


// Translations
const TEXTS = {
  es: {
    title: 'Tu Perfil de Aspiraciones',
    subtitle: 'Ayúdanos a entender tus metas para los próximos 12 meses',
    question: 'Pregunta',
    of: 'de',
    next: 'Siguiente',
    prev: 'Anterior',
    submit: 'Enviar',
    skip: 'Omitir',
    required: 'Requerido',
    optional: 'Opcional',
    loading: 'Cargando...',
    submitting: 'Enviando...',
    maxSelected: 'Máximo alcanzado',
    selected: 'seleccionados',
    
    // Success
    successTitle: '¡Gracias por compartir tus aspiraciones!',
    successDesc: 'Tu perfil objetivo ha sido calculado. Podrás ver cómo se alinea con los objetivos de la empresa.',
    viewAlignment: 'Ver mi alineación',
    goBack: 'Volver al inicio',
    
    // Commitment labels
    commitment: {
      '-2': 'Mucho menos comprometido',
      '-1': 'Algo menos comprometido',
      '0': 'Igual que hoy',
      '1': 'Algo más comprometido',
      '2': 'Mucho más comprometido'
    }
  },
  en: {
    title: 'Your Aspirations Profile',
    subtitle: 'Help us understand your goals for the next 12 months',
    question: 'Question',
    of: 'of',
    next: 'Next',
    prev: 'Previous',
    submit: 'Submit',
    skip: 'Skip',
    required: 'Required',
    optional: 'Optional',
    loading: 'Loading...',
    submitting: 'Submitting...',
    maxSelected: 'Maximum reached',
    selected: 'selected',
    
    // Success
    successTitle: 'Thank you for sharing your aspirations!',
    successDesc: 'Your objective profile has been calculated. You can see how it aligns with company goals.',
    viewAlignment: 'View my alignment',
    goBack: 'Go back',
    
    // Commitment labels
    commitment: {
      '-2': 'Much less committed',
      '-1': 'Somewhat less committed',
      '0': 'Same as today',
      '1': 'Somewhat more committed',
      '2': 'Much more committed'
    }
  }
};

export default function EmployeeAspirationalQuestionnaire({ 
  userId, 
  cicloId = null,
  onComplete,
  showTransition = true 
}) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = TEXTS[language] || TEXTS.es;
  
  const effectiveUserId = userId || user?.id;
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [transitionMessage, setTransitionMessage] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({
    capitales_prioritarios: [],
    importancia_financiera: 3,
    tipo_desarrollo_buscado: [],
    importancia_proposito: 3,
    tendencia_compromiso: 0,
    tendencia_compromiso_texto: '',
    texto_libre_aspiraciones: '',
    capitales_satisfechos_hoy: []
  });
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  // Load questions
  const loadQuestions = useCallback(async () => {
    if (!effectiveUserId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `/v5/empleado/${effectiveUserId}/objetivo/questions?lang=${language}`
      );
      
      if (response.data.success) {
        setQuestions(response.data.data.questions);
        setTransitionMessage(response.data.data.transition_message);
      }
    } catch (error) {
      logger.error('Error loading questions:', error);
      toast.error('Error al cargar las preguntas');
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, language]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  // Handle answer changes
  const handleMultipleChoiceChange = (questionId, value, maxSelections) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (current.includes(value)) {
        return { ...prev, [questionId]: current.filter(v => v !== value) };
      } else if (current.length < maxSelections) {
        return { ...prev, [questionId]: [...current, value] };
      }
      return prev;
    });
  };

  const handleScaleChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleTextChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  // Submit answers
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const response = await axios.post(
        `/v5/empleado/${effectiveUserId}/objetivo`,
        {
          ciclo_id: cicloId,
          ...answers
        }
      );
      
      if (response.data.success) {
        setResult(response.data.data);
        setSubmitted(true);
        toast.success(t.successTitle);
        
        if (onComplete) {
          onComplete(response.data.data);
        }
      }
    } catch (error) {
      logger.error('Error submitting:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar las respuestas');
    } finally {
      setSubmitting(false);
    }
  };

  // Validate current question
  const isCurrentQuestionValid = () => {
    const question = questions[currentQuestion];
    if (!question) return true;
    
    const answer = answers[question.id];
    
    if (!question.required) return true;
    
    switch (question.type) {
      case 'multiple_choice':
        return Array.isArray(answer) && answer.length > 0;
      case 'scale':
        return typeof answer === 'number';
      case 'commitment_scale':
        return typeof answers.tendencia_compromiso === 'number';
      case 'text':
        return true; // Optional
      default:
        return true;
    }
  };

  // Render question
  const renderQuestion = (question) => {
    switch (question.type) {
      case 'multiple_choice':
        return renderMultipleChoice(question);
      case 'scale':
        return renderScale(question);
      case 'commitment_scale':
        return renderCommitmentScale(question);
      case 'text':
        return renderText(question);
      default:
        return null;
    }
  };

  const renderMultipleChoice = (question) => {
    const selected = answers[question.id] || [];
    const isMaxReached = selected.length >= question.max_selections;
    
    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">{question.help}</span>
          <Badge variant={isMaxReached ? 'default' : 'outline'}>
            {selected.length}/{question.max_selections} {t.selected}
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {question.options.map((option) => {
            const isSelected = selected.includes(option.value);
            const isDisabled = !isSelected && isMaxReached;
            
            return (
              <div
                key={option.value}
                onClick={() => !isDisabled && handleMultipleChoiceChange(question.id, option.value, question.max_selections)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-50' 
                    : isDisabled 
                      ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                      : 'border-gray-200 hover:border-blue-300'
                }`}
                data-testid={`option-${option.value}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                  }`}>
                    {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                  </div>
                  <span className={isSelected ? 'font-medium' : ''}>{option.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderScale = (question) => {
    const value = answers[question.id] || question.min;
    
    return (
      <div className="space-y-6 py-4">
        <div className="flex justify-between text-sm text-gray-600">
          <span>{question.labels?.[question.min]}</span>
          <span>{question.labels?.[question.max]}</span>
        </div>
        
        <Slider
          value={[value]}
          onValueChange={([v]) => handleScaleChange(question.id, v)}
          min={question.min}
          max={question.max}
          step={1}
          className="py-4"
          data-testid={`scale-${question.id}`}
        />
        
        <div className="flex justify-center">
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
            <span className="text-2xl font-bold text-blue-600">{value}</span>
            <span className="text-sm text-gray-500">/ {question.max}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderCommitmentScale = (question) => {
    const value = answers.tendencia_compromiso;
    
    return (
      <div className="space-y-6">
        <RadioGroup
          value={String(value)}
          onValueChange={(v) => handleScaleChange('tendencia_compromiso', parseInt(v))}
          className="space-y-3"
        >
          {question.options.map((option) => (
            <div
              key={option.value}
              className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                value === option.value 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => handleScaleChange('tendencia_compromiso', option.value)}
            >
              <RadioGroupItem value={String(option.value)} id={`commitment-${option.value}`} />
              <Label htmlFor={`commitment-${option.value}`} className="flex-1 cursor-pointer">
                <span className={value === option.value ? 'font-medium' : ''}>{option.label}</span>
              </Label>
              {option.value > 0 && <TrendingUp className="w-5 h-5 text-green-500" />}
              {option.value < 0 && <TrendingUp className="w-5 h-5 text-red-500 rotate-180" />}
            </div>
          ))}
        </RadioGroup>
        
        {question.has_text_field && (
          <div className="space-y-2 pt-4">
            <Label className="text-sm text-gray-600">{question.help}</Label>
            <Textarea
              value={answers.tendencia_compromiso_texto || ''}
              onChange={(e) => handleTextChange('tendencia_compromiso_texto', e.target.value)}
              placeholder="..."
              rows={2}
              className="resize-none"
              data-testid="commitment-text"
            />
          </div>
        )}
      </div>
    );
  };

  const renderText = (question) => (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-gray-400" />
        <span className="text-sm text-gray-500">{question.help}</span>
      </div>
      <Textarea
        value={answers[question.id] || ''}
        onChange={(e) => handleTextChange(question.id, e.target.value)}
        placeholder="..."
        maxLength={question.max_length}
        rows={4}
        className="resize-none"
        data-testid={`text-${question.id}`}
      />
      <div className="text-right text-sm text-gray-400">
        {(answers[question.id] || '').length}/{question.max_length}
      </div>
    </div>
  );

  // Success screen
  if (submitted && result) {
    return (
      <Card className="max-w-2xl mx-auto" data-testid="questionnaire-success">
        <CardContent className="pt-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-3">{t.successTitle}</h2>
          <p className="text-gray-600 mb-8">{t.successDesc}</p>
          
          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => onComplete && onComplete(result)}>
              {t.goBack}
            </Button>
            <Button onClick={() => window.location.href = '/dashboard'}>
              {t.viewAlignment}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const isLastQuestion = currentQuestion === questions.length - 1;

  return (
    <div className="max-w-3xl mx-auto py-6 px-4" data-testid="aspirational-questionnaire">
      {/* Header with transition message */}
      {showTransition && currentQuestion === 0 && transitionMessage && (
        <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white rounded-full shadow-sm">
              <Sparkles className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t.title}</h3>
              <p className="text-gray-600">{transitionMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-500 mb-2">
          <span>{t.question} {currentQuestion + 1} {t.of} {questions.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>
      
      {/* Question Card */}
      {currentQ && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">{currentQ.text}</CardTitle>
                  <Badge variant={currentQ.required ? 'default' : 'secondary'} className="mt-2">
                    {currentQ.required ? t.required : t.optional}
                  </Badge>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {renderQuestion(currentQ)}
          </CardContent>
        </Card>
      )}
      
      {/* Navigation */}
      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
          disabled={currentQuestion === 0}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          {t.prev}
        </Button>
        
        {isLastQuestion ? (
          <Button 
            onClick={handleSubmit}
            disabled={submitting || !isCurrentQuestionValid()}
            data-testid="submit-questionnaire"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t.submitting}
              </>
            ) : (
              <>
                {t.submit}
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        ) : (
          <Button 
            onClick={() => setCurrentQuestion(prev => prev + 1)}
            disabled={!isCurrentQuestionValid()}
          >
            {t.next}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
